import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MapTrifold, DownloadSimple, ArrowLeft, X } from "@phosphor-icons/react";
import { Survey, Telemetry, Photos, saveBlob, photoSrc } from "../lib/api";
import { LocalStore } from "../lib/storage";
import { SurveyMap } from "../components/SurveyMap";
import { AltitudeChart } from "../components/AltitudeChart";
import { SurveyReportButton } from "../components/SurveyReport";

const safe = (s) => (s || "survey").replace(/[^a-zA-Z0-9-_]+/g, "-");

const Stat = ({ id, label, value, unit }) => (
  <div className="px-4 py-3 border-r-2 border-black last:border-r-0" data-testid={`survey-stat-${id}`}>
    <p className="label-mono text-[10px] text-neutral-500">{label}</p>
    <p className="font-mono text-2xl font-bold tabular-nums">{value ?? "—"}<span className="text-xs ml-1">{value != null ? unit : ""}</span></p>
  </div>
);

export default function SurveyPage() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get("session") || LocalStore.getActiveSession()?.id;
  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [photo, setPhoto] = useState(null);
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!sessionId) { setLoading(false); setMissing(true); return; }
    Survey.get(sessionId).then(setSurvey)
      .catch((e) => (e?.response?.status === 404 ? setMissing(true) : toast.error("Could not load survey")))
      .finally(() => setLoading(false));
  }, [sessionId]);

  const select = async (n, open) => {
    setSelected(n);
    if (!open) return;
    const p = survey.points.find((q) => q.n === n);
    try { setPhoto({ ...p, full: await Photos.get(sessionId, p.id) }); } catch { toast.error("Could not open photo"); }
  };

  const dlKml = async () => saveBlob(new Blob([await Survey.kml(sessionId)], { type: "application/vnd.google-earth.kml+xml" }), `${safe(survey.name)}.kml`);
  const dlGeo = async () => saveBlob(new Blob([JSON.stringify(await Telemetry.geojson(sessionId), null, 2)], { type: "application/geo+json" }), `${safe(survey.name)}.geojson`);

  if (loading) return <p className="label-mono p-6" data-testid="survey-loading">Loading survey…</p>;
  if (!survey || survey.count === 0) {
    return (
      <div className="px-4 py-10 max-w-2xl mx-auto text-center" data-testid="survey-empty">
        <p className="font-display text-3xl font-black mb-3" data-testid="survey-empty-title">{missing ? "SESSION NOT FOUND" : "NO GPS IN THIS SESSION"}</p>
        <p className="text-sm text-neutral-600 mb-6">{missing ? "Open a session from the gallery to view its survey." : "Import drone photos with GPS, or apply a DJI .SRT / CSV flight log in the gallery."}</p>
        <button onClick={() => nav("/gallery")} className="btn-flat" data-testid="survey-go-gallery">Go to Gallery</button>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 max-w-6xl mx-auto page-fade" data-testid="survey-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div className="min-w-0">
          <button onClick={() => nav("/gallery")} className="label-mono text-neutral-500 flex items-center gap-1 hover:text-black" data-testid="survey-back-btn"><ArrowLeft size={12} weight="bold" /> GALLERY</button>
          <h1 className="font-display text-3xl sm:text-5xl font-black tracking-tighter truncate flex items-center gap-3"><MapTrifold weight="bold" /> {survey.name}</h1>
          <p className="text-xs font-mono text-neutral-600 mt-1">FLIGHT PATH FROM {survey.path_source === "flight_log" ? "IMPORTED FLIGHT LOG" : "PHOTO CAPTURE ORDER"}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <SurveyReportButton sessionId={sessionId} name={survey.name} />
          <button onClick={dlKml} className="btn-flat" data-testid="survey-download-kml-btn"><DownloadSimple weight="bold" size={18} /> KML</button>
          <button onClick={dlGeo} className="btn-flat-outline" data-testid="survey-download-geojson-btn"><DownloadSimple weight="bold" size={18} /> GeoJSON</button>
        </div>
      </div>

      <SurveyMap survey={survey} selected={selected} onSelect={select} />

      <div className="grid grid-cols-2 sm:grid-cols-5 border-2 border-t-0 border-black bg-white" data-testid="survey-stats">
        <Stat id="photos" label="PHOTOS" value={survey.count} />
        <Stat id="acres" label="AREA (EST.)" value={survey.acres} unit="ac" />
        <Stat id="area-m2" label="AREA (EST.)" value={survey.area_m2?.toLocaleString()} unit="m²" />
        <Stat id="alt-avg" label="AVG ALTITUDE" value={survey.alt_avg} unit="m" />
        <Stat id="alt-max" label="MAX ALTITUDE" value={survey.alt_max} unit="m" />
      </div>
      <p className="text-[11px] font-mono text-neutral-500 mt-1 mb-6" data-testid="survey-estimate-note">{survey.estimate_note}</p>

      <AltitudeChart survey={survey} selected={selected} onSelect={select} />

      {photo && <PhotoSheet photo={photo} onClose={() => setPhoto(null)} />}
    </div>
  );
}

function PhotoSheet({ photo, onClose }) {
  const src = photoSrc(photo.full);
  return createPortal(
    <div className="fixed inset-0 z-[1200] bg-black/90 flex flex-col" data-testid="survey-photo-sheet">
      <div className="flex items-center justify-between px-4 py-3 text-white border-b-2 border-white/20">
        <p className="font-mono text-xs truncate">#{photo.n} · {photo.filename} · {photo.lat.toFixed(6)}, {photo.lon.toFixed(6)}{photo.alt != null && ` · ${photo.alt} m`}</p>
        <button onClick={onClose} className="text-white" data-testid="survey-photo-close-btn"><X size={22} weight="bold" /></button>
      </div>
      <div className="flex-1 min-h-0 flex items-center justify-center p-4">
        {src ? <img src={src} alt={photo.filename} className="max-w-full max-h-full object-contain border-2 border-white" data-testid="survey-photo-image" />
          : <p className="text-white font-mono text-sm">No preview available for this file type.</p>}
      </div>
    </div>,
    document.body
  );
}
